<!--
  OrgInstructorClassrooms — 기관 관리 › 강사관리 › 강사별 클래스 목록
  · GET /voucher/org/{org}/instructors/{users_idx}/classrooms?class_status= (진행 중 지정 요약 assignments 포함)
  · 클래스 상세는 기존 AcademicClassesDetail 을 조회 전용(?view=org)으로 재사용
-->
<template>
  <div class="p-page">
    <a-page-head
      :title="instructor ? `${instructor.name} 강사의 클래스` : '강사별 클래스'"
      :breadcrumbs="[{ label: '기관 관리' }, { label: '강사관리', to: { name: 'OrgInstructors' } }, { label: instructor ? instructor.name : '클래스' }]"
    >
      <template #actions>
        <a-btn kind="ghost" size="md" @click="$router.push({ name: 'OrgInstructors' })">
          <template #icon>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          </template>
          강사 목록
        </a-btn>
        <a-btn kind="ghost" size="md" @click="loadClassrooms">새로고침</a-btn>
      </template>
    </a-page-head>

    <org-selector />

    <div v-if="instructor" class="p-meta-bar">
      <a-pill :kind="instructor.role === 'master' ? 'yellow' : 'default'">{{ instructor.role === 'master' ? 'Master' : '강사' }}</a-pill>
      <span class="p-meta-bar__item p-mono">{{ instructor.email }}</span>
      <span class="p-meta-bar__item">진행 중 클래스 {{ instructor.ongoing_class_count == null ? 0 : instructor.ongoing_class_count }}개 · 수강생 {{ instructor.student_count == null ? 0 : instructor.student_count }}명</span>
    </div>

    <a-card bare padding="16" class="p-toolbar">
      <div class="p-tabs">
        <button
          v-for="t in statusTabs"
          :key="t.key"
          class="p-tab"
          :class="{ 'is-active': status === t.key }"
          @click="status = t.key"
        >{{ t.label }}</button>
      </div>
      <span class="p-cell-sub">클래스를 누르면 조회 전용 상세로 이동합니다</span>
    </a-card>

    <a-card title="클래스 목록" :sub="`총 ${classrooms.length}개`">
      <div v-if="loading" class="p-loader"><div class="p-spinner" /></div>
      <a-empty v-else-if="!classrooms.length" title="클래스가 없습니다" sub="다른 상태 탭을 확인해 보세요." />
      <a-table v-else :columns="cols" :rows="classrooms" row-key="classroom_idx">
        <template #cell-name="{ row }">
          <div class="p-cell-class is-link" @click="goDetail(row)">
            <div class="p-avatar">{{ (row.name || 'C').charAt(0) }}</div>
            <div>
              <div class="p-cell-title">{{ row.name }}</div>
              <div class="p-cell-sub p-mono">#{{ row.classroom_idx }}<template v-if="row.instructor_name"> · {{ row.instructor_name }}</template></div>
            </div>
          </div>
        </template>
        <template #cell-period="{ row }">
          <span class="p-mono">{{ fmtDate(row.start_date) }} ~ {{ fmtDate(row.end_date) }}</span>
        </template>
        <template #cell-class_status="{ value }">
          <a-pill :kind="classStatusKind(value)">{{ classStatusLabel(value) }}</a-pill>
        </template>
        <template #cell-student_count="{ value }"><span class="p-mono">{{ value == null ? 0 : value }}</span></template>
        <template #cell-assignments="{ row }">
          <div v-if="row.assignments && row.assignments.length" class="p-chips">
            <span v-for="a in row.assignments" :key="a.classroom_voucher_idx" class="p-chip" :title="`기한 ${fmtDate(a.register_until)}`">
              {{ a.title }} · {{ gradeLabel(a.grade) }} · {{ a.registered_count == null ? 0 : a.registered_count }}명
            </span>
          </div>
          <span v-else class="p-cell-sub">-</span>
        </template>
        <template #cell-actions="{ row }">
          <div class="p-row-actions">
            <a-btn size="sm" kind="ghost" @click="goDetail(row)">상세</a-btn>
            <a-btn v-if="row.class_status === 'ongoing'" size="sm" kind="primary" @click="goAssign(row)">도서 지정</a-btn>
          </div>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script>
import APageHead from '@/components/Pineapple/APageHead.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import ATable from '@/components/Pineapple/ATable.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';
import OrgSelector from './components/OrgSelector.vue';
import { fmtDate, gradeLabel, classStatusKind, classStatusLabel, voucherErrorText } from './orgUtils';

export default {
  name: 'OrgInstructorClassrooms',
  components: { APageHead, ACard, ATable, APill, ABtn, AEmpty, OrgSelector },
  data() {
    return {
      status: 'ongoing',
      classrooms: [],
      instructors: [],
      loading: false,
      statusTabs: [
        { key: 'ongoing',   label: '진행 중' },
        { key: 'closed',    label: '종료' },
        { key: 'cancelled', label: '취소' },
        { key: 'all',       label: '전체' },
      ],
      cols: [
        { key: 'name',          label: '클래스' },
        { key: 'period',        label: '기간',       width: '200px' },
        { key: 'class_status',  label: '상태',       width: '90px' },
        { key: 'student_count', label: '수강생',     width: '80px',  align: 'right' },
        { key: 'assignments',   label: '진행 중 지정', wrap: true },
        { key: 'actions',       label: '',           width: '170px', align: 'right' },
      ],
    };
  },
  computed: {
    orgIdx() { return this.$store.getters['organization/currentIdx']; },
    usersIdx() { return this.$route.query.users_idx ? Number(this.$route.query.users_idx) : null; },
    instructor() { return this.instructors.find(i => i.users_idx === this.usersIdx) || null; },
  },
  watch: {
    orgIdx() { this.loadAll(); },
    usersIdx() { this.loadAll(); },
    status() { this.loadClassrooms(); },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    fmtDate,
    gradeLabel,
    classStatusKind,
    classStatusLabel,
    loadAll() {
      this.loadInstructors();
      this.loadClassrooms();
    },
    async loadInstructors() {
      if (!this.orgIdx) return;
      try {
        const res = await this.$axios({ method: 'get', url: `/voucher/org/${this.orgIdx}/instructors`, loadingBar: false });
        this.instructors = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.instructors = [];
      }
    },
    async loadClassrooms() {
      if (!this.orgIdx || !this.usersIdx) return;
      this.loading = true;
      try {
        const res = await this.$axios({
          method: 'get',
          url: `/voucher/org/${this.orgIdx}/instructors/${this.usersIdx}/classrooms`,
          params: { class_status: this.status },
          loadingBar: false,
        });
        this.classrooms = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.classrooms = [];
        this.$notifyVue({ type: 'error', title: voucherErrorText(e, '클래스 목록을 불러오지 못했습니다.') });
      } finally {
        this.loading = false;
      }
    },
    goDetail(row) {
      // 기존 클래스 상세 화면을 조회 전용으로 재사용 — 쓰기 동작은 화면에서 숨기고 서버도 담당 강사 검사로 막는다
      this.$router.push({ name: 'AcademicClassesDetail', query: { idx: row.classroom_idx, view: 'org' } });
    },
    goAssign(row) {
      this.$router.push({ name: 'OrgVouchers', query: { tab: 'assign', classroom_idx: row.classroom_idx } });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './_org.scss';
</style>
